import { useState } from "react";
import { DISPLAY } from "../constants.js";
import { NumberField, ImageGrid } from "./ComposeLayers.js";
import RichTextDialog from "./RichTextDialog.js";
import "./ComposeLayers.css";
import "./ComposeSequence.css";

export const IN_EFFECT_OPTIONS = [
  ["fade", "Fade in"],
  ["slide-left", "Slide from left"],
  ["slide-right", "Slide from right"],
  ["slide-up", "Slide up"],
  ["slide-down", "Slide down"],
  ["zoom-in", "Zoom in"],
  ["zoom-out", "Zoom out"],
  ["blur", "Blur in"],
  ["pop", "Pop"],
  ["rotate", "Rotate in"],
  ["flip", "Flip in"],
  ["reveal", "Reveal (wipe)"],
];

export const OUT_EFFECT_OPTIONS = [
  ["fade", "Fade out"],
  ["slide-left", "Slide out left"],
  ["slide-right", "Slide out right"],
  ["slide-up", "Slide out up"],
  ["slide-down", "Slide out down"],
  ["zoom-in", "Grow out"],
  ["zoom-out", "Shrink out"],
  ["blur", "Blur out"],
  ["pop", "Pop out"],
  ["rotate", "Rotate out"],
  ["flip", "Flip out"],
  ["reveal", "Wipe out"],
];

export const EMPHASIS_EFFECT_OPTIONS = [
  ["pulse", "Pulse"],
  ["shake", "Shake"],
  ["wiggle", "Wiggle"],
  ["flash", "Flash"],
  ["bounce", "Bounce"],
  ["tada", "Tada"],
];

const MAX_FRAMES = 12;

function SecondsField({ label, value, editable, onChange }) {
  return (
    <NumberField
      label={label}
      value={value}
      onChange={(v) => {
        if (!editable) return;
        onChange(Math.max(0, Math.min(30, Math.round(v * 10) / 10)));
      }}
    />
  );
}

/** Rotator editor: cycle multiple typed/image frames in one layer (manual timing). */
export default function SequenceEditor({ layer, editable, images, allowInfinite, onUpdateLayer, onUploadImage, isUploadingImage, uploadError, palette }) {
  const [openDialogForIndex, setOpenDialogForIndex] = useState(null);
  const seq = layer.sequence;
  if (!seq) return null;
  const isText = layer.type === "text";

  // Mutations derive from the LATEST sequence at apply time (avoids stale
  // closures when several edits land before a re-render).
  const mutateSeq = (fn) => onUpdateLayer(layer.id, (l) => (l.sequence ? { ...l, sequence: fn(l.sequence) } : l));

  const updateFrame = (i, patch) =>
    mutateSeq((s) => ({ ...s, frames: s.frames.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) }));
  const updateIn = (i, patch) =>
    mutateSeq((s) => ({
      ...s,
      frames: s.frames.map((f, idx) => (idx === i ? { ...f, in: { ...f.in, ...patch } } : f)),
    }));
  const updateOut = (i, patch) =>
    mutateSeq((s) => ({
      ...s,
      frames: s.frames.map((f, idx) => {
        if (idx !== i) return f;
        const cur = f.out ?? { effect: "fade", start: 2, duration: 0.5 };
        return { ...f, out: { ...cur, ...patch } };
      }),
    }));
  const setEmphasis = (i, effect) =>
    mutateSeq((s) => ({
      ...s,
      frames: s.frames.map((f, idx) =>
        idx === i
          ? { ...f, emphasis: effect === "none" ? undefined : { effect, times: f.emphasis?.times ?? 2 } }
          : f
      ),
    }));
  const setEmphasisTimes = (i, times) =>
    mutateSeq((s) => ({
      ...s,
      frames: s.frames.map((f, idx) => (idx === i && f.emphasis ? { ...f, emphasis: { ...f.emphasis, times } } : f)),
    }));
  const addFrame = () =>
    mutateSeq((s) => {
      // Chain the new frame AFTER the previous one leaves (no overlap), and
      // give the previously-last frame an exit so it hands off cleanly.
      const frames = [...s.frames];
      const last = frames.length - 1;
      const prev = frames[last];
      const prevInEnd = prev.in.delay + prev.in.duration;
      const out = prev.out ?? { effect: "fade", start: prevInEnd + 1, duration: 0.5 };
      frames[last] = { ...prev, out };
      const appearAt = out.start + out.duration;
      frames.push({
        text: isText ? "New message" : undefined,
        in: { effect: "fade", delay: appearAt, duration: 0.5 },
      });
      const cycleDuration = Math.min(DISPLAY.PROFILE.MAX_ANIMATION_S, Math.max(s.cycleDuration, appearAt + 0.5 + 1));
      return { ...s, frames, cycleDuration };
    });
  const removeFrame = (i) => mutateSeq((s) => ({ ...s, frames: s.frames.filter((_, idx) => idx !== i) }));

  return (
    <div className="sequence-editor">
      <p className="sequence-editor__title">Sequence · {isText ? "texts" : "images"}</p>

      <div className="sequence-editor__grid-2">
        <SecondsField
          label="Cycle length (s)"
          value={seq.cycleDuration}
          editable={editable}
          onChange={(v) => mutateSeq((s) => ({ ...s, cycleDuration: Math.max(0.1, v) }))}
        />
        <label className="layers-panel__field">
          <span>Loops</span>
          <select
            value={seq.loops}
            disabled={!editable}
            onChange={(e) => mutateSeq((s) => ({ ...s, loops: Number(e.target.value) }))}
            className="layers-panel__input"
          >
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n}×
              </option>
            ))}
            {allowInfinite ? <option value={0}>∞ forever</option> : null}
          </select>
        </label>
      </div>
      <p className="sequence-editor__note">
        {seq.loops <= 0
          ? "Loops forever (self-hosted). Frames rotate continuously in order."
          : `Total ${(seq.cycleDuration * seq.loops).toFixed(1)}s. Frames play in order each cycle; the last frame holds at the end.`}
      </p>

      {seq.frames.map((frame, i) => {
        const isLast = i === seq.frames.length - 1;
        return (
          <div key={i} className="sequence-editor__frame">
            <div className="sequence-editor__frame-header">
              <span className="sequence-editor__frame-title">
                Frame {i + 1}
                {isLast ? <span className="sequence-editor__frame-final"> · final (holds)</span> : null}
              </span>
              <button
                type="button"
                disabled={!editable || seq.frames.length <= 1}
                onClick={() => removeFrame(i)}
                className="sequence-editor__remove-btn"
              >
                Remove
              </button>
            </div>

            {isText ? (
              <div className="layers-panel__field">
                <div className="layers-panel__text-preview">
                  {(frame.text ?? "").split("\n").map((line, li, arr) => (
                    <span key={li}>
                      {line || <em>(empty line)</em>}
                      {li < arr.length - 1 ? <br /> : null}
                    </span>
                  ))}
                  {(frame.words?.length > 0 && frame.words.some((w) => w.color)) || frame.colorRanges?.length > 0 ? (
                    <span className="layers-panel__text-preview-color-dot" aria-label="Has colored text" />
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled={!editable}
                  onClick={() => setOpenDialogForIndex(i)}
                  className="layers-panel__link-btn"
                >
                  Edit text →
                </button>
                {openDialogForIndex === i ? (
                  <RichTextDialog
                    title={`Editing frame ${i + 1} text`}
                    initialText={frame.text ?? ""}
                    initialColorRanges={frame.colorRanges ?? []}
                    initialWords={frame.words ?? []}
                    paletteColors={palette ?? []}
                    onSave={({ text, colorRanges, words }) => {
                      updateFrame(i, { text, colorRanges, words });
                      setOpenDialogForIndex(null);
                    }}
                    onClose={() => setOpenDialogForIndex(null)}
                  />
                ) : null}
              </div>
            ) : (
              <ImageGrid
                selected={frame.imageSource ?? null}
                images={images}
                onPick={(source) => updateFrame(i, { imageSource: source ?? undefined })}
                onUploadImage={onUploadImage}
                isUploadingImage={isUploadingImage}
                uploadError={uploadError}
                disabled={!editable}
              />
            )}

            {/* Entrance timing */}
            <div className="sequence-editor__timing-block">
              <label className="sequence-editor__timing-label">In</label>
              <select
                value={frame.in.effect}
                disabled={!editable}
                onChange={(e) => updateIn(i, { effect: e.target.value })}
                className="layers-panel__input"
              >
                {IN_EFFECT_OPTIONS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
              <div className="sequence-editor__grid-2 sequence-editor__grid-2--tight">
                <SecondsField label="Appear at (s)" value={frame.in.delay} editable={editable} onChange={(v) => updateIn(i, { delay: v })} />
                <SecondsField label="Duration (s)" value={frame.in.duration} editable={editable} onChange={(v) => updateIn(i, { duration: v })} />
              </div>
            </div>

            {/* Emphasis — attention beats while the frame is held */}
            <div className="sequence-editor__timing-block">
              <label className="sequence-editor__timing-label">
                Emphasis <span className="sequence-editor__timing-label-faint">(while held)</span>
              </label>
              <div className="sequence-editor__grid-2">
                <select
                  value={frame.emphasis?.effect ?? "none"}
                  disabled={!editable}
                  onChange={(e) => setEmphasis(i, e.target.value)}
                  className="layers-panel__input"
                >
                  <option value="none">None</option>
                  {EMPHASIS_EFFECT_OPTIONS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                {frame.emphasis ? (
                  <label className="layers-panel__field">
                    <span>Beats</span>
                    <select
                      value={frame.emphasis.times}
                      disabled={!editable}
                      onChange={(e) => setEmphasisTimes(i, Number(e.target.value))}
                      className="layers-panel__input"
                    >
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <option key={n} value={n}>
                          {n}×
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
            </div>

            {/* Exit timing — the final frame holds instead */}
            {isLast ? (
              <p className="sequence-editor__note">Stays on screen until the play ends.</p>
            ) : (
              <div className="sequence-editor__timing-block">
                <label className="sequence-editor__timing-label">Out</label>
                <select
                  value={frame.out?.effect ?? "fade"}
                  disabled={!editable}
                  onChange={(e) => updateOut(i, { effect: e.target.value })}
                  className="layers-panel__input"
                >
                  {OUT_EFFECT_OPTIONS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                <div className="sequence-editor__grid-2 sequence-editor__grid-2--tight">
                  <SecondsField label="Leave at (s)" value={frame.out?.start ?? 2} editable={editable} onChange={(v) => updateOut(i, { start: v })} />
                  <SecondsField label="Duration (s)" value={frame.out?.duration ?? 0.5} editable={editable} onChange={(v) => updateOut(i, { duration: v })} />
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        disabled={!editable || seq.frames.length >= MAX_FRAMES}
        onClick={addFrame}
        className="sequence-editor__add-btn"
      >
        + Add {isText ? "text" : "image"} frame
      </button>
    </div>
  );
}